import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2, Plus, Copy, Ban, CheckCircle2, Clock, XCircle, Loader2,
  MessageCircle, Users, FileText, Eye, ThumbsUp, ThumbsDown,
  AlertTriangle, Camera, MapPin, DollarSign, Shield, Globe, Hash,
} from 'lucide-react';
import { Button, Card, CardBody, Input } from '../../components/ui';
import {
  useInviteLinks, useInviteStats, useCreateInviteLink, useRevokeInviteLink,
  usePendingKyc, useApproveUser, useRejectUser, useResetUser,
  usePublicLinks, usePublicLinkStats,
} from '../../hooks/useInviteLinks';
import { api } from '../../lib/api';

type Tab = 'links' | 'public' | 'kyc';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  ACTIVE: { label: 'Ativo', color: 'text-yellow-400 bg-yellow-500/20', icon: Clock },
  USED: { label: 'Utilizado', color: 'text-green-400 bg-green-500/20', icon: CheckCircle2 },
  EXPIRED: { label: 'Expirado', color: 'text-surface-400 bg-surface-500/20', icon: Clock },
  REVOKED: { label: 'Revogado', color: 'text-red-400 bg-red-500/20', icon: XCircle },
};

const USER_TYPE_LABELS: Record<string, string> = {
  CUSTOMER: 'Correntista',
  EMPLOYEE: 'Funcionário',
  SUPPLIER: 'Fornecedor',
};

const DOC_TYPE_LABELS: Record<string, { label: string; icon: any }> = {
  // Fallback codes
  ID_FRONT: { label: 'RG/CNH (Frente)', icon: FileText },
  ID_BACK: { label: 'RG/CNH (Verso)', icon: FileText },
  SELFIE: { label: 'Selfie com Documento', icon: Camera },
  ADDRESS_PROOF: { label: 'Comprovante de Endereço', icon: MapPin },
  INCOME_PROOF: { label: 'Comprovante de Renda', icon: DollarSign },
  // Tenant-configured codes
  ID_DOCUMENT: { label: 'Documento de Identidade', icon: FileText },
  SELFIE_WITH_ID: { label: 'Selfie com Documento', icon: Camera },
  PROOF_OF_RESIDENCE: { label: 'Comprovante de Residência', icon: MapPin },
  PROOF_OF_INCOME: { label: 'Comprovante de Renda', icon: DollarSign },
};

export function InviteLinks() {
  const [tab, setTab] = useState<Tab>('links');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Links de Convite</h1>
          <p className="text-surface-400 mt-1">Gerencie convites individuais, links públicos e revise documentos KYC</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-800/50 rounded-xl w-fit">
        <button
          onClick={() => setTab('links')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'links' ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-surface-200'
          }`}
        >
          <Link2 className="w-4 h-4" />
          Individuais
        </button>
        <button
          onClick={() => setTab('public')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'public' ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-surface-200'
          }`}
        >
          <Globe className="w-4 h-4" />
          Links Públicos
        </button>
        <button
          onClick={() => setTab('kyc')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'kyc' ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-surface-200'
          }`}
        >
          <Shield className="w-4 h-4" />
          Revisão KYC
        </button>
      </div>

      {tab === 'links' && <LinksTab />}
      {tab === 'public' && <PublicLinksTab />}
      {tab === 'kyc' && <KycTab />}
    </div>
  );
}


// ===== LINKS TAB (Individual) =====
function LinksTab() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [personName, setPersonName] = useState('');
  const [description, setDescription] = useState('');
  const [targetUserType, setTargetUserType] = useState('CUSTOMER');
  const [accountType, setAccountType] = useState('PF');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<{ token: string; personName: string } | null>(null);
  const [createError, setCreateError] = useState('');

  const { data, isLoading } = useInviteLinks(page);
  const { data: stats } = useInviteStats();
  const createMutation = useCreateInviteLink();
  const revokeMutation = useRevokeInviteLink();

  const baseUrl = import.meta.env.VITE_PUBLIC_URL || window.location.origin;
  const publicUrl = import.meta.env.VITE_PUBLIC_URL || 'https://finteck.sasolucoes.com';
  const getFullInviteUrl = (token: string) => `${baseUrl}/invite/${token}`;
  const getPublicInviteUrl = (token: string) => `${publicUrl}/invite/${token}`;

  const shareOnWhatsApp = (token: string, name: string) => {
    const url = getPublicInviteUrl(token);
    const message = `Olá ${name}! 👋\n\nVocê foi convidado(a) para se cadastrar na *Invest Redenção*.\n\nClique no link abaixo para criar sua conta:\n\n${url}\n\n⚠️ Este link é de uso único e pessoal.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCreate = async () => {
    if (!personName.trim()) return;
    setCreateError('');
    try {
      const result = await createMutation.mutateAsync({
        personName: personName.trim(),
        description: description || `Convite para ${personName.trim()}`,
        targetUserType,
        accountType,
        isPublic: false,
      });
      setCreatedLink({ token: result.token, personName: personName.trim() });
      setPersonName('');
      setDescription('');
      setTargetUserType('CUSTOMER');
      setAccountType('PF');
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || 'Erro ao criar link');
    }
  };

  const handleCopy = (token: string, id: string) => {
    navigator.clipboard.writeText(getFullInviteUrl(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <>
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => { setShowCreate(true); setCreatedLink(null); setCreateError(''); }}>
          <Plus className="w-4 h-4 mr-2" /> Criar Link Individual
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total, icon: Link2, color: 'text-surface-300' },
            { label: 'Aguardando', value: stats.active, icon: Clock, color: 'text-yellow-400' },
            { label: 'Realizado', value: stats.used, icon: CheckCircle2, color: 'text-green-400' },
            { label: 'Expirados', value: stats.expired, icon: Clock, color: 'text-surface-400' },
            { label: 'Revogados', value: stats.revoked, icon: XCircle, color: 'text-red-400' },
          ].map((s) => (
            <Card key={s.label} variant="glass">
              <CardBody className="p-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div>
                  <p className="text-2xl font-bold text-surface-100">{s.value}</p>
                  <p className="text-xs text-surface-400">{s.label}</p>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Create Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <Card variant="glass">
              <CardBody className="p-6 space-y-4">
                {!createdLink ? (
                  <>
                    <h3 className="text-lg font-semibold text-surface-100">Gerar Link Individual</h3>
                    <p className="text-sm text-surface-400">Link de uso único para uma pessoa específica.</p>
                    {createError && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">{createError}</div>
                    )}
                    <Input label="Nome da Pessoa *" placeholder="Ex: João Silva" value={personName} onChange={(e) => setPersonName(e.target.value)} />
                    <Input label="Observação (opcional)" placeholder="Ex: Indicação do gerente Carlos" value={description} onChange={(e) => setDescription(e.target.value)} />
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">Tipo de Conta</label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setAccountType('PF')}
                          className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                            accountType === 'PF'
                              ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                              : 'bg-surface-900 border-surface-700 text-surface-400 hover:border-surface-600'
                          }`}
                        >
                          <div className="text-center">
                            <p className="font-semibold">Pessoa Física</p>
                            <p className="text-xs mt-1 opacity-70">CPF — Documentos pessoais</p>
                          </div>
                        </button>
                        <button
                          onClick={() => setAccountType('PJ')}
                          className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                            accountType === 'PJ'
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                              : 'bg-surface-900 border-surface-700 text-surface-400 hover:border-surface-600'
                          }`}
                        >
                          <div className="text-center">
                            <p className="font-semibold">Pessoa Jurídica</p>
                            <p className="text-xs mt-1 opacity-70">CNPJ — Contrato social + docs</p>
                          </div>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">Tipo de Usuário</label>
                      <select value={targetUserType} onChange={(e) => setTargetUserType(e.target.value)} className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-200 focus:border-primary-500 focus:outline-none">
                        <option value="CUSTOMER">Correntista</option>
                        <option value="EMPLOYEE">Funcionário</option>
                        <option value="SUPPLIER">Fornecedor</option>
                      </select>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
                      <Button variant="primary" onClick={handleCreate} isLoading={createMutation.isPending} disabled={!personName.trim()}>Criar Link</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center space-y-3">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-surface-100">Link Criado!</h3>
                      <p className="text-sm text-surface-400">Convite para <span className="text-surface-200 font-medium">{createdLink.personName}</span></p>
                    </div>
                    <div className="p-3 bg-surface-800 rounded-lg flex items-center gap-2">
                      <input type="text" readOnly value={getFullInviteUrl(createdLink.token)} className="flex-1 bg-transparent text-surface-300 text-sm outline-none" />
                      <button onClick={() => handleCopy(createdLink.token, 'created')} className="p-2 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors">
                        {copiedId === 'created' ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="primary" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => shareOnWhatsApp(createdLink.token, createdLink.personName)}>
                        <MessageCircle className="w-4 h-4 mr-2" /> Enviar via WhatsApp
                      </Button>
                      <Button variant="ghost" onClick={() => handleCopy(createdLink.token, 'created')}>
                        <Copy className="w-4 h-4 mr-2" /> Copiar
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <Button variant="ghost" size="sm" onClick={() => { setCreatedLink(null); setShowCreate(false); }}>Fechar</Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Links Table */}
      <Card variant="glass">
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary-400" /></div>
          ) : !data?.items?.length ? (
            <div className="text-center p-12">
              <Link2 className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <p className="text-surface-400">Nenhum link individual criado ainda</p>
              <Button variant="primary" className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" /> Criar Primeiro Link
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-800">
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Status</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Pessoa</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Link</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Tipo</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Cadastrou-se</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Data</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((invite: any) => {
                    const statusCfg = STATUS_CONFIG[invite.status] || STATUS_CONFIG.ACTIVE;
                    const StatusIcon = statusCfg.icon;
                    return (
                      <tr key={invite.id} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            <StatusIcon className="w-3 h-3" /> {statusCfg.label}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-surface-200">
                          <span className="font-medium">{invite.personName || '-'}</span>
                          {invite.description && <p className="text-xs text-surface-500 mt-0.5">{invite.description}</p>}
                        </td>
                        <td className="p-4 text-sm max-w-[200px]">
                          <span className="text-primary-400 text-xs truncate block cursor-pointer hover:text-primary-300" title={getFullInviteUrl(invite.token)} onClick={() => handleCopy(invite.token, invite.id + '-link')}>
                            {getFullInviteUrl(invite.token).replace(/^https?:\/\//, '').slice(0, 40)}...
                          </span>
                          {copiedId === invite.id + '-link' && <span className="text-xs text-green-400">Copiado!</span>}
                        </td>
                        <td className="p-4 text-sm text-surface-300">{USER_TYPE_LABELS[invite.targetUserType] || invite.targetUserType}</td>
                        <td className="p-4 text-sm text-surface-300">
                          {invite.usedBy ? (
                            <div>
                              <span className="text-green-400 font-medium">{invite.usedBy.name}</span>
                              <p className="text-xs text-surface-500">{invite.usedBy.email}</p>
                            </div>
                          ) : <span className="text-surface-500">Aguardando...</span>}
                        </td>
                        <td className="p-4 text-xs text-surface-400">{new Date(invite.createdAt).toLocaleDateString('pt-BR')}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {invite.status === 'ACTIVE' && (
                              <>
                                <button onClick={() => shareOnWhatsApp(invite.token, invite.personName || 'Convidado')} className="p-1.5 rounded-lg hover:bg-green-500/20 text-surface-400 hover:text-green-400 transition-colors" title="WhatsApp">
                                  <MessageCircle className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleCopy(invite.token, invite.id)} className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors" title="Copiar">
                                  {copiedId === invite.id ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                </button>
                                <button onClick={() => revokeMutation.mutate(invite.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-surface-400 hover:text-red-400 transition-colors" title="Revogar">
                                  <Ban className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-surface-800">
              <p className="text-sm text-surface-400">Página {data.page} de {data.totalPages} ({data.total} links)</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button variant="ghost" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}


// ===== PUBLIC LINKS TAB =====
function PublicLinksTab() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [description, setDescription] = useState('');
  const [targetUserType, setTargetUserType] = useState('CUSTOMER');
  const [maxUses, setMaxUses] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<{ token: string; description: string } | null>(null);
  const [createError, setCreateError] = useState('');

  const { data, isLoading } = usePublicLinks(page);
  const { data: stats } = usePublicLinkStats();
  const createMutation = useCreateInviteLink();
  const revokeMutation = useRevokeInviteLink();

  const baseUrl = import.meta.env.VITE_PUBLIC_URL || window.location.origin;
  const publicUrl = import.meta.env.VITE_PUBLIC_URL || 'https://finteck.sasolucoes.com';
  const getFullInviteUrl = (token: string) => `${baseUrl}/invite/${token}`;
  const getPublicInviteUrl = (token: string) => `${publicUrl}/invite/${token}`;

  const shareOnWhatsApp = (token: string, desc: string) => {
    const url = getPublicInviteUrl(token);
    const message = `👋 Abra sua conta na *Invest Redenção*!\n\nClique no link abaixo para se cadastrar:\n\n${url}\n\n✅ Cadastro rápido e seguro.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCreate = async () => {
    if (!description.trim()) return;
    setCreateError('');
    try {
      const result = await createMutation.mutateAsync({
        description: description.trim(),
        targetUserType,
        isPublic: true,
        maxUses: maxUses ? parseInt(maxUses) : undefined,
      });
      setCreatedLink({ token: result.token, description: description.trim() });
      setDescription('');
      setTargetUserType('CUSTOMER');
      setMaxUses('');
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || 'Erro ao criar link');
    }
  };

  const handleCopy = (token: string, id: string) => {
    navigator.clipboard.writeText(getFullInviteUrl(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <>
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => { setShowCreate(true); setCreatedLink(null); setCreateError(''); }}>
          <Plus className="w-4 h-4 mr-2" /> Criar Link Público
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Links Públicos', value: stats.total, icon: Globe, color: 'text-surface-300' },
            { label: 'Ativos', value: stats.active, icon: CheckCircle2, color: 'text-green-400' },
            { label: 'Cadastros', value: stats.totalRegistrations, icon: Users, color: 'text-primary-400' },
            { label: 'Revogados', value: stats.revoked, icon: XCircle, color: 'text-red-400' },
          ].map((s) => (
            <Card key={s.label} variant="glass">
              <CardBody className="p-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div>
                  <p className="text-2xl font-bold text-surface-100">{s.value}</p>
                  <p className="text-xs text-surface-400">{s.label}</p>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Create Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <Card variant="glass">
              <CardBody className="p-6 space-y-4">
                {!createdLink ? (
                  <>
                    <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-primary-400" /> Gerar Link Público
                    </h3>
                    <p className="text-sm text-surface-400">
                      Link de uso múltiplo — várias pessoas podem usar o mesmo link para se cadastrar.
                      Cada pessoa passará pelo processo de KYC e ficará pendente até aprovação.
                    </p>
                    {createError && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">{createError}</div>
                    )}
                    <Input label="Descrição do Link *" placeholder="Ex: Campanha WhatsApp Março, Site institucional..." value={description} onChange={(e) => setDescription(e.target.value)} />
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">Tipo de Usuário</label>
                      <select value={targetUserType} onChange={(e) => setTargetUserType(e.target.value)} className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-200 focus:border-primary-500 focus:outline-none">
                        <option value="CUSTOMER">Correntista</option>
                        <option value="EMPLOYEE">Funcionário</option>
                        <option value="SUPPLIER">Fornecedor</option>
                      </select>
                    </div>
                    <Input
                      label="Limite de Cadastros (opcional)"
                      placeholder="Deixe vazio para ilimitado"
                      type="number"
                      min="2"
                      value={maxUses}
                      onChange={(e) => setMaxUses(e.target.value)}
                    />
                    <p className="text-xs text-surface-500">Se não informar, o link aceita cadastros ilimitados.</p>
                    <div className="flex gap-3 justify-end">
                      <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
                      <Button variant="primary" onClick={handleCreate} isLoading={createMutation.isPending} disabled={!description.trim()}>
                        <Globe className="w-4 h-4 mr-2" /> Criar Link Público
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center space-y-3">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                        <Globe className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-surface-100">Link Público Criado!</h3>
                      <p className="text-sm text-surface-400">{createdLink.description}</p>
                    </div>
                    <div className="p-3 bg-surface-800 rounded-lg flex items-center gap-2">
                      <input type="text" readOnly value={getFullInviteUrl(createdLink.token)} className="flex-1 bg-transparent text-surface-300 text-sm outline-none" />
                      <button onClick={() => handleCopy(createdLink.token, 'pub-created')} className="p-2 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors">
                        {copiedId === 'pub-created' ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="primary" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => shareOnWhatsApp(createdLink.token, createdLink.description)}>
                        <MessageCircle className="w-4 h-4 mr-2" /> Compartilhar via WhatsApp
                      </Button>
                      <Button variant="ghost" onClick={() => handleCopy(createdLink.token, 'pub-created')}>
                        <Copy className="w-4 h-4 mr-2" /> Copiar
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <Button variant="ghost" size="sm" onClick={() => { setCreatedLink(null); setShowCreate(false); }}>Fechar</Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Public Links Table */}
      <Card variant="glass">
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary-400" /></div>
          ) : !data?.items?.length ? (
            <div className="text-center p-12">
              <Globe className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <p className="text-surface-400">Nenhum link público criado ainda</p>
              <p className="text-surface-500 text-sm mt-1">Links públicos permitem que várias pessoas se cadastrem usando o mesmo link.</p>
              <Button variant="primary" className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" /> Criar Link Público
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-800">
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Status</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Descrição</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Link</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Tipo</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Cadastros</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Data</th>
                    <th className="text-left p-4 text-xs font-medium text-surface-400 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((link: any) => {
                    const statusCfg = STATUS_CONFIG[link.status] || STATUS_CONFIG.ACTIVE;
                    const StatusIcon = statusCfg.icon;
                    const usagePercent = link.maxUses > 0 && link.maxUses < 999999
                      ? Math.round((link.currentUses / link.maxUses) * 100)
                      : null;
                    return (
                      <tr key={link.id} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            <StatusIcon className="w-3 h-3" /> {statusCfg.label}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-surface-200">
                          <span className="font-medium">{link.description || 'Link Público'}</span>
                          <p className="text-xs text-surface-500 mt-0.5">Criado por {link.createdBy?.name}</p>
                        </td>
                        <td className="p-4 text-sm max-w-[200px]">
                          <span className="text-primary-400 text-xs truncate block cursor-pointer hover:text-primary-300" title={getFullInviteUrl(link.token)} onClick={() => handleCopy(link.token, link.id + '-link')}>
                            {getFullInviteUrl(link.token).replace(/^https?:\/\//, '').slice(0, 40)}...
                          </span>
                          {copiedId === link.id + '-link' && <span className="text-xs text-green-400">Copiado!</span>}
                        </td>
                        <td className="p-4 text-sm text-surface-300">{USER_TYPE_LABELS[link.targetUserType] || link.targetUserType}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-primary-400" />
                            <span className="text-sm font-medium text-surface-200">{link.currentUses}</span>
                            {link.maxUses < 999999 && (
                              <span className="text-xs text-surface-500">/ {link.maxUses}</span>
                            )}
                            {link.maxUses >= 999999 && (
                              <span className="text-xs text-surface-500">ilimitado</span>
                            )}
                          </div>
                          {usagePercent !== null && (
                            <div className="mt-1 h-1.5 bg-surface-700 rounded-full overflow-hidden w-20">
                              <div className={`h-full rounded-full ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-xs text-surface-400">{new Date(link.createdAt).toLocaleDateString('pt-BR')}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {link.status === 'ACTIVE' && (
                              <>
                                <button onClick={() => shareOnWhatsApp(link.token, link.description || 'Link Público')} className="p-1.5 rounded-lg hover:bg-green-500/20 text-surface-400 hover:text-green-400 transition-colors" title="WhatsApp">
                                  <MessageCircle className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleCopy(link.token, link.id)} className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors" title="Copiar">
                                  {copiedId === link.id ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                </button>
                                <button onClick={() => revokeMutation.mutate(link.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-surface-400 hover:text-red-400 transition-colors" title="Revogar">
                                  <Ban className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-surface-800">
              <p className="text-sm text-surface-400">Página {data.page} de {data.totalPages} ({data.total} links)</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button variant="ghost" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}


// ===== KYC TAB =====
function KycTab() {
  const [page, setPage] = useState(1);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [rejectUserId, setRejectUserId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewDoc, setPreviewDoc] = useState<{ id: string; mimeType: string; name: string } | null>(null);

  const { data, isLoading, refetch } = usePendingKyc(page);
  const approveMutation = useApproveUser();
  const rejectMutation = useRejectUser();
  const resetMutation = useResetUser();

  const handleApprove = async (userId: string) => {
    try {
      await approveMutation.mutateAsync(userId);
      refetch();
    } catch { /* handled */ }
  };

  const handleReject = async () => {
    if (!rejectUserId) return;
    try {
      await rejectMutation.mutateAsync({ userId: rejectUserId, reason: rejectReason || 'Documentos insuficientes' });
      setRejectUserId(null);
      setRejectReason('');
      refetch();
    } catch { /* handled */ }
  };

  const handleReset = async (userId: string) => {
    try {
      await resetMutation.mutateAsync(userId);
      refetch();
    } catch { /* handled */ }
  };

  const openDocPreview = (doc: any) => {
    if (doc.mimeType.startsWith('image/')) {
      setPreviewDoc({ id: doc.id, mimeType: doc.mimeType, name: doc.originalName });
    } else {
      api.get(`/invite-links/document-file/${doc.id}`, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.data], { type: doc.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        });
    }
  };

  return (
    <>
      {/* Reject Modal */}
      <AnimatePresence>
        {rejectUserId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setRejectUserId(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-surface-900 border border-surface-700 rounded-2xl p-6 w-full max-w-md space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <ThumbsDown className="w-5 h-5 text-red-400" /> Rejeitar Documentos
              </h3>
              <p className="text-sm text-surface-400">Informe o motivo da rejeição. O usuário poderá reenviar os documentos.</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Documento ilegível, foto cortada..."
                className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-200 focus:border-red-500 focus:outline-none resize-none h-24"
              />
              <div className="flex gap-3 justify-end">
                <Button variant="ghost" onClick={() => setRejectUserId(null)}>Cancelar</Button>
                <Button variant="primary" className="bg-red-600 hover:bg-red-700" onClick={handleReject} isLoading={rejectMutation.isPending}>
                  Rejeitar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {previewDoc && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setPreviewDoc(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="relative max-w-3xl max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewDoc(null)}
                className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm"
              >
                Fechar ✕
              </button>
              <p className="text-white/70 text-sm mb-2">{previewDoc.name}</p>
              <DocImage documentId={previewDoc.id} alt={previewDoc.name} className="max-h-[70vh] rounded-lg object-contain" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error messages */}
      {(approveMutation.error || rejectMutation.error || resetMutation.error) && (
        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {(approveMutation.error as any)?.response?.data?.message ||
           (rejectMutation.error as any)?.response?.data?.message ||
           (resetMutation.error as any)?.response?.data?.message || 'Erro ao processar'}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary-400" /></div>
      ) : !data?.items?.length ? (
        <Card variant="glass">
          <CardBody className="text-center p-12">
            <Users className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400">Nenhum usuário com documentos pendentes de revisão</p>
            <p className="text-surface-500 text-sm mt-1">Quando alguém se cadastrar via link (individual ou público) e enviar documentos, aparecerá aqui.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-surface-400">{data.total} usuário(s) aguardando revisão de documentos</p>

          {data.items.map((user: any) => {
            const isExpanded = expandedUser === user.userId;
            return (
              <Card key={user.userId} variant="glass">
                <CardBody className="p-0">
                  {/* User header */}
                  <button
                    onClick={() => setExpandedUser(isExpanded ? null : user.userId)}
                    className="w-full flex items-center justify-between p-4 hover:bg-surface-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-surface-100">{user.name}</p>
                        <p className="text-xs text-surface-400">{user.email} {user.cpfCnpj && `• ${user.cpfCnpj}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-surface-500">{user.documents?.length || 0} doc(s)</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.status === 'REJECTED'
                          ? 'text-red-400 bg-red-500/20'
                          : 'text-yellow-400 bg-yellow-500/20'
                      }`}>
                        {user.status === 'REJECTED' ? 'Rejeitado' : 'Pendente'}
                      </span>
                      <span className="text-surface-500 text-sm">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </button>

                  {/* Expanded: documents */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-3 border-t border-surface-800">
                          <p className="text-xs text-surface-500 pt-3">
                            Cadastrado em {new Date(user.createdAt).toLocaleDateString('pt-BR')} • Tipo: {USER_TYPE_LABELS[user.type] || user.type}
                            {user.phone && ` • Tel: ${user.phone}`}
                          </p>

                          {/* Documents grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {user.documents?.map((doc: any) => {
                              const docInfo = DOC_TYPE_LABELS[doc.documentType] || { label: doc.documentType, icon: FileText };
                              const DocIcon = docInfo.icon;
                              return (
                                <div key={doc.id} className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg border border-surface-700">
                                  <div className="w-8 h-8 rounded-lg bg-surface-700 flex items-center justify-center flex-shrink-0">
                                    <DocIcon className="w-4 h-4 text-surface-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-surface-200">{docInfo.label}</p>
                                    <p className="text-xs text-surface-500 truncate">{doc.originalName}</p>
                                    <p className="text-xs text-surface-600">{(doc.fileSize / 1024).toFixed(0)} KB</p>
                                  </div>
                                  <button
                                    onClick={() => openDocPreview(doc)}
                                    className="p-2 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-primary-400 transition-colors"
                                    title="Visualizar"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>

                          {(!user.documents || user.documents.length === 0) && (
                            <p className="text-sm text-surface-500 text-center py-4">Nenhum documento enviado ainda</p>
                          )}

                          {/* Actions */}
                          <div className="flex gap-3 pt-2">
                            {user.status === 'REJECTED' ? (
                              <Button
                                variant="primary"
                                className="flex-1"
                                onClick={() => handleReset(user.userId)}
                                isLoading={resetMutation.isPending}
                              >
                                <Shield className="w-4 h-4 mr-2" /> Liberar Reenvio de Documentos
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="primary"
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApprove(user.userId)}
                                  isLoading={approveMutation.isPending}
                                >
                                  <ThumbsUp className="w-4 h-4 mr-2" /> Aprovar e Ativar Conta
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                  onClick={() => { setRejectUserId(user.userId); setRejectReason(''); }}
                                >
                                  <ThumbsDown className="w-4 h-4 mr-2" /> Rejeitar
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardBody>
              </Card>
            );
          })}

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-surface-400">Página {data.page} de {data.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button variant="ghost" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ===== DocImage: fetches image with auth header =====
function DocImage({ documentId, alt, className }: { documentId: string; alt: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    api.get(`/invite-links/document-file/${documentId}`, { responseType: 'blob' })
      .then((res) => {
        if (!revoked) {
          const blobUrl = URL.createObjectURL(res.data);
          setSrc(blobUrl);
        }
      })
      .catch(() => { if (!revoked) setSrc(null); });
    return () => { revoked = true; };
  }, [documentId]);

  if (!src) return <div className={`bg-surface-800 flex items-center justify-center ${className}`}><Loader2 className="w-6 h-6 animate-spin text-surface-500" /></div>;
  return <img src={src} alt={alt} className={className} />;
}
